import { i18n } from "../i18n/i18n";

import React, { Component } from 'react'
import { capitalize, find, map, remove } from 'lodash'
import { produce } from 'immer'
import contactSiteService from '../services/contactSite.service'
import customerService from '../services/customer.service'
import vendorService from '../services/vendor.service'
import { ContactableType } from '../apis/contact.api'
import customerApi, { CustomerData } from '../apis/customer.api'
import vendorApi, { VendorData } from '../apis/vendor.api'
import { ContactSiteData } from '../apis/contactSite.api'
import { ActionName } from '../components/Actions'
import Heading from '../components/Heading'
import Row from '../components/Row/Row'
import Select, { SelectOption } from '../components/Select'
import TableHorizontal, {
  TableHorizontalData, TableHorizontalHeader
} from '../components/TableHorizontal'
import Button from '../components/Button'
import InputText from '../components/InputText'
import SearchPicker, { SearchPickerOption } from '../components/SearchPicker'

type ContactableData = CustomerData | VendorData

export interface ItemContactInputData {
  contact: ContactableData
  site: ContactSiteData
  partNumber?: string
}

interface ItemContactInputProps {
  type: ContactableType
  addeds: ItemContactInputData[]
  onChange: (type: ContactableType, data: ItemContactInputData[]) => void
  onAdd?: (type: ContactableType, data: ItemContactInputData) => void
  onDelete?: (type: ContactableType, data: ItemContactInputData) => void
}

interface ItemContactInputState {
  contact: string
  selectedContact: ContactableData | null
  site: string
  partNumber: string
  contacts: ContactableData[]
  sites: ContactSiteData[]
}

type ContactableApi = typeof customerApi | typeof vendorApi
type ContactableService = typeof customerService | typeof vendorService

type ContactableApiRecord = Record<ContactableType, ContactableApi>
type ContactableServiceRecord = Record<ContactableType, ContactableService>

export default class ItemContactInput
  extends Component<ItemContactInputProps, ItemContactInputState> {
  contactableApis: ContactableApiRecord = {
    customer: customerApi,
    vendor: vendorApi
  }

  contactableServices: ContactableServiceRecord = {
    customer: customerService,
    vendor: vendorService
  }

  constructor (props: ItemContactInputProps) {
    super(props)
    this.state = {
      contact: '',
      selectedContact: null,
      site: '',
      partNumber: '',
      contacts: [],
      sites: []
    }
    this.handleContactSearch = this.handleContactSearch.bind(this)
    this.handleContactSelect = this.handleContactSelect.bind(this)
    this.handleContactBlur = this.handleContactBlur.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleAdd = this.handleAdd.bind(this)
    this.handleActionsClick = this.handleActionsClick.bind(this)
  }

  get contactLabel () {
    return capitalize(this.props.type)
  }

  get contactSiteLabel () {
    const { type } = this.props
    return `${capitalize(type)} site`
  }

  get contactableApi () {
    return this.contactableApis[this.props.type]
  }

  get contactableService () {
    return this.contactableServices[this.props.type]
  }

  get headers (): TableHorizontalHeader[] {
    const contactLabel = this.contactLabel
    const contactSiteLabel = this.contactSiteLabel
    return [
      { heading: contactLabel, path: 'contact.name' },
      { heading: contactSiteLabel, path: 'site.name' },
      { heading: i18n("Part number"), path: 'partNumber' },
      { heading: i18n("Actions") }
    ]
  }

  get addedData (): TableHorizontalData<ItemContactInputData>[] {
    return map(this.props.addeds, added => {
      const id = added.site.id
      return { id, ...added, actions: ['delete'] }
    })
  }

  get contactOptions () {
    return this.contactableService.searchOptions(this.state.contacts)
  }

  get sites (): SelectOption[] {
    return contactSiteService.selectOptions(this.state.sites)
  }

  listSites (id: number) {
    this.contactableApi.listSites(id, sites => {
      if (!sites) return
      this.setState({ sites })
    })
  }

  handleContactSearch (name: string, term: string) {
    this.contactableApi.search(term, contacts => {
      if (!contacts) return
      this.setState({ contacts })
    })
  }

  handleContactSelect (name: string, option: SearchPickerOption) {
    const id = option.id as number
    const contact = this.contactableService.find(this.state.contacts, id)
    if (contact) {
      this.setState({ selectedContact: contact })
      this.listSites(contact.id)
    }
    this.setState({ contacts: [] })
  }

  handleContactBlur () {
    this.setState({ contacts: [] })
  }

  handleInputChange (name: string, value: string) {
    if (name === 'contact') {
      this.setState({ selectedContact: null, contact: value })
    } else if (name === 'site') this.setState({ site: value })
    else if (name === 'partNumber') this.setState({ partNumber: value })
  }

  pushAdded (
    contact: ContactableData,
    site: ContactSiteData,
    partNumber: string
  ) {
    const addedData = { contact, site, partNumber }
    const exists = find(this.props.addeds, added => {
      const isSameContact = added.contact.id === contact.id
      const isSameSite = added.site.id === site.id
      return isSameContact && isSameSite
    })
    if (exists) {
      this.updateAdded(addedData)
    } else {
      const { onAdd } = this.props
      const addeds = produce(this.props.addeds, addeds => {
        addeds.push(addedData)
      })
      this.props.onChange(this.props.type, addeds)
      if (onAdd) onAdd(this.props.type, addedData)
    }
  }

  updateAdded (updatedData: ItemContactInputData) {
    const { onDelete, onAdd } = this.props
    const addeds = produce(this.props.addeds, addeds => {
      remove(addeds, added => (added.site.id === updatedData.site.id))
      addeds.push(updatedData)
    })
    this.props.onChange(this.props.type, addeds)
    if (onDelete) onDelete(this.props.type, updatedData)
    if (onAdd) onAdd(this.props.type, updatedData)
  }

  removeAdded (id: number) {
    const deletedData = find(this.props.addeds, added => (added.site.id === id))
    const addeds = produce(this.props.addeds, addeds => {
      remove(addeds, added => (added.site.id === id))
    })
    this.props.onChange(this.props.type, addeds)
    if (deletedData) {
      const { onDelete } = this.props
      if (onDelete) onDelete(this.props.type, deletedData)
    }
  }

  clearFields () {
    this.setState({
      contact: '',
      selectedContact: null,
      site: '',
      partNumber: '',
      contacts: [],
      sites: []
    })
  }

  handleAdd () {
    const { selectedContact } = this.state
    const selectedSiteId = Number(this.state.site)
    const site = contactSiteService.find(this.state.sites, selectedSiteId)
    if (!selectedContact || !site) return
    this.pushAdded(selectedContact, site, this.state.partNumber)
    this.clearFields()
  }

  handleActionsClick (id: number, action: ActionName) {
    if (action === 'delete') this.removeAdded(id)
  }

  render () {
    return (
      <div>
        <Heading text={this.contactLabel} />
        <div className="mt-2">
          <TableHorizontal
            headers={this.headers}
            data={this.addedData}
            onActionClick={this.handleActionsClick} />
          <Row column={4}>
            <SearchPicker
              name="contact"
              value={this.state.contact}
              options={this.contactOptions}
              label={this.contactLabel}
              onSearch={this.handleContactSearch}
              onChange={this.handleInputChange}
              onSelect={this.handleContactSelect}
              onBlur={this.handleContactBlur} />
            <Select
              name="site"
              value={this.state.site}
              options={this.sites}
              label={this.contactSiteLabel}
              onChange={this.handleInputChange} />
            <InputText
              name="partNumber"
              value={this.state.partNumber}
              label={i18n("Part number")}
              onChange={this.handleInputChange} />
            <div className="flex items-end mb-4">
              <Button
                text={i18n("Add")}
                full={true}
                onClick={this.handleAdd} />
            </div>
          </Row>
        </div>
      </div>
    )
  }
}
